import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Text, Heading, Button, Input } from "..";
import * as XLSX from 'xlsx';

const truncateData = (data, limit) => {
	if (data.length > limit) {
		return [...data.slice(0, limit), { financing_no: "..." }];
	}
	return data;
};

const TableRow = ({ poolId, data, prevData, prevFinancingAmount, financingAmount, total_financing_amount_pool, oc_value, onChangeValue }) => {
	const displayPrevData = truncateData(prevData, 5);
	const displayData = truncateData(data, 5);
	const [poolValue, setPoolValue] = useState();
	const [ocValue, setOCValue] = useState();
	const currPoolValue = useMemo(
		() => total_financing_amount_pool - prevFinancingAmount + financingAmount,
		[total_financing_amount_pool, prevFinancingAmount, financingAmount]
	);

	const _updateValueElm = useCallback((poolValue, ocValue) => {
		setOCValue(ocValue);
		setPoolValue(poolValue);
	}, []);

	const _updateOcValue = useCallback((val) => {
		const poolValue = Math.round(currPoolValue + (val * currPoolValue * .01));
		const ocValue = val;
		onChangeValue && onChangeValue(poolValue, ocValue);
		_updateValueElm(poolValue, ocValue);
	}, [_updateValueElm, onChangeValue, currPoolValue]);

	const _updatePoolValue = useCallback((val) => {
		const poolValue = val;
		const ocValue = Math.round((val - currPoolValue) / currPoolValue * 100);
		onChangeValue && onChangeValue(poolValue, ocValue);
		_updateValueElm(poolValue, ocValue);
	}, [_updateValueElm, onChangeValue, currPoolValue]);

	useEffect(() => {
		const currOcValue = 0;
		_updateValueElm(currPoolValue, currOcValue);
	}, [_updateValueElm, currPoolValue, oc_value]);

	return (
		<div className="flex items-center justify-center w-full hover:bg-gray-50_02 py-5 gap-1">
			<div className='w-[18%] flex justify-center'>
				<Text as="p" className="!text-blue_gray-900_06 tracking-[-0.14px]">
					{ poolId }
				</Text>
			</div>
			<div className='w-[18%] flex flex-col items-center'>
				{ displayPrevData.map((item, index) => (
					<Text key={ index } as="p" className="!text-blue_gray-900_06 tracking-[-0.14px]">
						{ item.financing_no }
					</Text>
				)) }
			</div>
			<div className='w-[18%] flex flex-col items-center'>
				{ displayData.map((item, index) => (
					<Text key={ index } as="p" className="!text-blue_gray-900_06 tracking-[-0.14px]">
						{ item.financing_no }
					</Text>
				)) }
			</div>
			<div className='w-[13%] flex justify-center'>
				<Text as="p" className="!text-blue_gray-900_06 tracking-[-0.14px]">
					{ prevFinancingAmount }
				</Text>
			</div>
			<div className='w-[13%] flex justify-center'>
				<Text as="p" className="!text-blue_gray-900_06 tracking-[-0.14px]">
					{ financingAmount }
				</Text>
			</div>
			<div className='w-[10%] flex justify-center'>
				<Input
					placeholder="Input Pool Value"
					value={ poolValue }
					onChange={ _updatePoolValue }
					type="number"
					className="gap-px border-gray-300 border-2 border-solid !rounded-[5px]"
				/>
			</div>
			<div className='w-[10%] flex justify-center'>
				<Input
					placeholder="Input Pool Value"
					value={ ocValue }
					onChange={ _updateOcValue }
					type="number"
					className="gap-px border-gray-300 border-2 border-solid !rounded-[5px]"
					suffix={ <Text size="xl" className="!text-blue_gray-700">%</Text> }
				/>
			</div>
		</div>
	);
};

const downloadData = (data, totalReplacement) => {
	const flattenedData = [];

	data.forEach(item => {
		const poolId = item.poolId;
		const prevFinancingAmount = item.prevFinancingAmount;
		const financingAmount = totalReplacement.find(replacement => replacement.poolId === item.poolId)?.totalReplacementAmount || 0;

		const maxLength = Math.max(item.prevData.length, item.data.length);
		for (let i = 0; i < maxLength; i++) {
			flattenedData.push({
				Pool_Id: i === 0 ? poolId : '',
				Previous_Financing_No: item.prevData[i]?.financing_no || '',
				Updated_Financing_No: item.data[i]?.financing_no || '',
				Previous_Financing_Amount: i === 0 ? prevFinancingAmount : '',
				Updated_Financing_Amount: i === 0 ? financingAmount : '',
			});
		}
	});

	const worksheet = XLSX.utils.json_to_sheet(flattenedData);
	const workbook = XLSX.utils.book_new();
	XLSX.utils.book_append_sheet(workbook, worksheet, "Data");
	XLSX.writeFile(workbook, "resume_redeem.xlsx");
};

const TableResumeRedeem = ({ data, totalReplacement, onChangValue }) => {
	return (
		<div className="flex flex-col items-center justify-start w-full gap-5">
			<Heading size="3xl" as="h1">Resume Replace Redeem </Heading>
			<div className='w-full bg-white-A700 shadow-2xl rounded-[10px] p-5 flex flex-col gap-10 '>
				<div className="flex flex-col  w-full ">
					<div className="flex items-center justify-center w-full pb-5">
						<div className='w-[18%] flex justify-center items-center gap-1'>
							<Text as="p" className="!font-bold !text-blue_gray-200 tracking-[-0.14px]">
								Pool ID
							</Text>
						</div>
						<div className='w-[18%] flex justify-center items-center gap-1'>
							<Text as="p" className="!font-bold !text-blue_gray-200 tracking-[-0.14px] ">
								Previous SAG
							</Text>
						</div>
						<div className='w-[18%] flex justify-center items-center gap-1'>
							<Text as="p" className="!font-bold !text-blue_gray-200 tracking-[-0.14px]">
								Updated SAG
							</Text>
						</div>
						<div className='w-[13%] flex justify-center items-center gap-1'>
							<Text as="p" className="!font-bold !text-blue_gray-200 tracking-[-0.14px]">
								Total Financing Redemption
							</Text>
						</div>
						<div className='w-[13%] flex justify-center items-center gap-1'>
							<Text as="p" className="!font-bold !text-blue_gray-200 tracking-[-0.14px]">
								Total Replacement
							</Text>
						</div>
						<div className='w-[10%] flex justify-center items-center gap-1'>
							<Text as="p" className="!font-bold !text-blue_gray-200 tracking-[-0.14px]">
								OC Level
							</Text>
						</div>
						<div className='w-[10%] flex justify-center items-center gap-1'>
							<Text as="p" className="!font-bold !text-blue_gray-200 tracking-[-0.14px]">
								OC Percentage
							</Text>
						</div>
					</div>
					<div className='h-px bg-orange-100 w-full'></div>
					{
						data &&
						<div className="flex flex-col items-center justify-start w-full pb-10 h-[50vh] overflow-y-auto">
							{ data.map((item, index) => (
								<div key={ index } className='w-full'>
									<TableRow
										{ ...item.pool }
										poolId={ item.poolId }
										prevData={ item.prevData }
										data={ item.data }
										prevFinancingAmount={ item.prevFinancingAmount }
										financingAmount={ totalReplacement.find(replacement => replacement.poolId === item.poolId)?.totalReplacementAmount || 0 }
										onChangeValue={ (poolValue, ocValue) => onChangValue({ poolId: item.poolId, poolValue, ocValue }) }
									/>
									<div className='h-px bg-orange-100 w-full'></div>
								</div>
							)) }
						</div>
					}
					<div className='h-px bg-orange-100 w-full'></div>
					{
						data &&
						<div className='flex justify-end w-full mt-3'>
							<Button onClick={ () => downloadData(data, totalReplacement) } color="yellow_800" size="md" className="!text-sm font-bold whitespace-nowrap w-fit">
								Download
							</Button>
						</div>
					}
				</div>
			</div>
		</div>
	);
};

export default TableResumeRedeem;